import { useEffect, useState } from 'react'
import { FaChevronDown, FaChevronRight, FaFolder, FaFolderOpen } from 'react-icons/fa'
import { BeatLoader } from 'react-spinners'
import { listFiles } from '../../../utils/fileBrowserApi'

function TreeNode({ bucket, node, activePrefix, onNavigate, depth }) {
  const [expanded, setExpanded] = useState(false)
  const [children, setChildren] = useState(null)
  const [loading, setLoading] = useState(false)

  const isActive = activePrefix === node.prefix

  const toggleExpand = async event => {
    event.stopPropagation()
    if (!expanded && children === null) {
      setLoading(true)
      try {
        const data = await listFiles({ bucket, prefix: node.prefix })
        setChildren(data.folders || [])
      } catch (err) {
        console.error('Error expanding folder:', err)
        setChildren([])
      } finally {
        setLoading(false)
      }
    }
    setExpanded(prev => !prev)
  }

  return (
    <div>
      <div
        className={`flex items-center gap-1 py-1.5 pr-2 rounded-lg cursor-pointer text-sm font-montserrat ${
          isActive ? 'bg-[#DEE6F0] font-semibold' : 'hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${depth * 16 + 8}px` }}
        onClick={() => onNavigate(node.prefix)}
      >
        <span
          className="w-4 shrink-0 text-gray-400 flex items-center justify-center"
          onClick={toggleExpand}
        >
          {loading ? (
            <BeatLoader color="#2b364b" size={3} />
          ) : expanded ? (
            <FaChevronDown size={10} />
          ) : (
            <FaChevronRight size={10} />
          )}
        </span>
        {isActive ? (
          <FaFolderOpen className="text-[#2b364b] shrink-0" size={14} />
        ) : (
          <FaFolder className="text-[#8ba3c7] shrink-0" size={14} />
        )}
        <span className="truncate text-gray-700" title={node.name}>
          {node.name}
        </span>
      </div>

      {expanded && children && (
        <div>
          {children.length === 0 && (
            <p
              className="text-xs text-gray-400 py-1 m-0"
              style={{ paddingLeft: `${(depth + 1) * 16 + 28}px` }}
            >
              No subfolders
            </p>
          )}
          {children.map(child => (
            <TreeNode
              key={child.prefix}
              bucket={bucket}
              node={child}
              activePrefix={activePrefix}
              onNavigate={onNavigate}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function FolderTree({ bucket, activePrefix, onNavigate }) {
  const [rootFolders, setRootFolders] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setRootFolders(null)
    setError(null)
    listFiles({ bucket, prefix: '' })
      .then(data => {
        if (!cancelled) setRootFolders(data.folders || [])
      })
      .catch(err => {
        console.error('Error loading folder tree:', err)
        if (!cancelled) setError(err.message)
      })
    return () => {
      cancelled = true
    }
  }, [bucket])

  return (
    <div className="font-montserrat">
      <div
        className={`flex items-center gap-2 py-1.5 px-2 rounded-lg cursor-pointer text-sm ${
          activePrefix === '' ? 'bg-[#DEE6F0] font-semibold' : 'hover:bg-gray-100'
        }`}
        onClick={() => onNavigate('')}
      >
        <FaFolderOpen className="text-[#2b364b] shrink-0" size={14} />
        <span className="text-gray-700">All files</span>
      </div>

      {error && <p className="text-xs text-red-500 px-2 py-1 m-0">{error}</p>}

      {rootFolders === null && !error && (
        <div className="flex justify-center py-3">
          <BeatLoader color="#2b364b" size={6} />
        </div>
      )}

      {rootFolders?.map(folder => (
        <TreeNode
          key={`${bucket}:${folder.prefix}`}
          bucket={bucket}
          node={folder}
          activePrefix={activePrefix}
          onNavigate={onNavigate}
          depth={0}
        />
      ))}
    </div>
  )
}
